// A única porta de rede do painel: toda chamada passa aqui com o token, e todo
// erro vira ErroDaApi com o código que o backend mandou (`{ error: { code,
// message } }`, ou a string solta do legado). 401 manda pro login.
export interface ErroDoServidor { code?: string; message?: string; [k: string]: unknown }

export class ErroDaApi extends Error {
  constructor(public readonly status: number, public readonly code: string, message: string, public readonly corpo: unknown) {
    super(message);
    this.name = 'ErroDaApi';
  }
}

export interface OpcoesDaChamada extends Omit<RequestInit, 'body'> {
  corpo?: unknown;
}

type ObterToken = () => Promise<string>;
type AoPerderSessao = () => void;

/** Fábrica: o app monta com o token do Firebase; o teste passa o que quiser. */
export function criarCliente({ obterToken, aoPerderSessao, fetchFn = fetch }: { obterToken: ObterToken; aoPerderSessao: AoPerderSessao; fetchFn?: typeof fetch }) {
  async function chamarCompleto<T>(caminho: string, opcoes: OpcoesDaChamada = {}): Promise<{ dados: T; cabecalhos: Headers }> {
    const token = await obterToken();
    const headers = new Headers(opcoes.headers);
    headers.set('Accept', 'application/json');
    if (token) headers.set('Authorization', 'Bearer ' + token);
    let body: BodyInit | undefined;
    if (opcoes.corpo !== undefined) {
      headers.set('Content-Type', 'application/json');
      body = JSON.stringify(opcoes.corpo);
    }
    const resposta = await fetchFn(caminho, { ...opcoes, headers, body });
    const texto = await resposta.text();
    let dados: unknown = null;
    try { dados = texto ? JSON.parse(texto) : null; } catch { dados = texto; }
    if (resposta.status === 401) aoPerderSessao();
    if (!resposta.ok) throw new ErroDaApi(resposta.status, ...codigoEMensagem(dados, resposta.status), dados);
    return { dados: dados as T, cabecalhos: resposta.headers };
  }
  const chamar = <T>(caminho: string, opcoes?: OpcoesDaChamada) => chamarCompleto<T>(caminho, opcoes).then((r) => r.dados);
  return {
    get: <T>(caminho: string, opcoes?: OpcoesDaChamada) => chamar<T>(caminho, { ...opcoes, method: 'GET' }),
    post: <T>(caminho: string, corpo?: unknown, opcoes?: OpcoesDaChamada) => chamar<T>(caminho, { ...opcoes, method: 'POST', corpo: corpo ?? {} }),
    /** Quando o servidor fala também pelo cabeçalho (X-Tema-Avisos, por exemplo). */
    postComCabecalhos: <T>(caminho: string, corpo?: unknown, opcoes?: OpcoesDaChamada) => chamarCompleto<T>(caminho, { ...opcoes, method: 'POST', corpo: corpo ?? {} }),
    put: <T>(caminho: string, corpo?: unknown, opcoes?: OpcoesDaChamada) => chamar<T>(caminho, { ...opcoes, method: 'PUT', corpo: corpo ?? {} }),
    patch: <T>(caminho: string, corpo?: unknown, opcoes?: OpcoesDaChamada) => chamar<T>(caminho, { ...opcoes, method: 'PATCH', corpo: corpo ?? {} }),
    delete: <T>(caminho: string, corpo?: unknown, opcoes?: OpcoesDaChamada) => chamar<T>(caminho, { ...opcoes, method: 'DELETE', corpo }),
  };
}
export type Cliente = ReturnType<typeof criarCliente>;

/** O envelope novo (`{ error: { code, message } }`), o antigo (`{ error: 'texto' }`) e o sem corpo. */
export function codigoEMensagem(dados: unknown, status: number): [string, string] {
  const erro = (dados as { error?: unknown } | null)?.error;
  if (erro && typeof erro === 'object') {
    const e = erro as ErroDoServidor;
    return [String(e.code || 'ERRO'), String(e.message || 'Algo deu errado.')];
  }
  if (typeof erro === 'string') return ['ERRO', erro];
  return [status === 401 ? 'UNAUTHORIZED' : 'ERRO', status === 401 ? 'Sua sessão venceu.' : 'Algo deu errado.'];
}
